import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse  } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ParkingInfo } from '../models/parkingInfo.model';
import { DisableDutyCallRange } from '../models/disableDutyCallRange.model';

export abstract class RestService<T> {
  protected url: string;
  headerOptions = new HttpHeaders({  
    'Accept': '*/*',
    'AccessCode': localStorage.getItem('AccessCode') ? localStorage.getItem('AccessCode') : "",
    'Content-Type': 'application/json'
  }); 

  constructor(protected _http: HttpClient, protected actionUrl:string){
    this.url = environment.baseUrl + actionUrl;
  }

  GetAll():Observable<T[]> {
    return this._http.get(this.url + '/get-all', {headers: this.headerOptions}) as Observable<T[]>;
  }

  GetAllWithVersions():Observable<T[]> {
    return this._http.get(this.url + '/get-all-with-version', {headers: this.headerOptions}) as Observable<T[]>;
  }
  
  GetByParkingId(id: any):Observable<T[]> {
    return this._http.get<any>(this.url + '/get-by-parking-id?parkingId='+ id, {headers: this.headerOptions}) as Observable<T[]>;
  }

  GetByClientId(id: any):Observable<T[]> {
    return this._http.get<any>(this.url + '/get-by-client-id?clientId='+ id, {headers: this.headerOptions}) as Observable<T[]>;
  }

  GetClientsByParkingId(id: any):Observable<T[]> {
    return this._http.get<any>(this.url + '/get-users?parkingId='+ id, {headers: this.headerOptions}) as Observable<T[]>;
  }

  GetConfigsByParkingId(id: any):Observable<T[]> {
    return this._http.get<any>(this.url + '/get-by-parkingId?parkingId='+ id, {headers: this.headerOptions}) as Observable<T[]>;
  }

  GetDisableDutyCallRange(id: string):Observable<T[]> {
    return this._http.get<any>(this.url + '/get-disable-duty-call-range?parkingId='+ id, {headers: this.headerOptions}) as Observable<T[]>;
  }

  GetParkingInfoByParkingId(id: any):Observable<T[]> {
    return this._http.get<any>(this.url + '/get-by-parkingId?parkingId='+ id, {headers: this.headerOptions}) as Observable<T[]>;
  }

  getСontentByParkingId(id: string, path: string):Observable<T[]> {
    return this._http.get<any>(this.url + '/get-content-by-parking-id?parkingId='+ id + "&path=" + path, {headers: this.headerOptions}) as Observable<T[]>;
  }

  GetReports(start: string, end: string, parkingId: string):Observable<T[]> {
    const startDate = new Date(start).toISOString();
    const endDate = new Date(end).toISOString();
    let url = this.url + '/get-report?start=' + startDate + '&end=' + endDate;
    if (parkingId !== 'all') {
      url += '&parkingId=' + parkingId;
    }
    return this._http.get<any>(url, {headers: this.headerOptions}) as Observable<T[]>;
  }

  download(parkingId: string, path: string, name: string): Observable<Blob> {
    const downloadUrl = this.url + '/download?parkingId=' + parkingId + '&path=' + encodeURIComponent(path) + '&name=' + encodeURIComponent(name);
    return this._http.get(downloadUrl, { headers: this.headerOptions, responseType: 'blob' });
  }

  upload(parkingId: string, path: string, file) {
    const formData: FormData = new FormData();
    formData.append('ParkingId', parkingId);
    formData.append('Path', path);
    formData.append('File', file);

    return this._http.post(this.url + '/upload-file', formData, { headers: {
      'Accept': '*/*',
      'AccessCode': localStorage.getItem('AccessCode') ? localStorage.getItem('AccessCode') : ""
    }});
  }
  
  singleUpdate(parkingInfo: ParkingInfo) {
    const headerOptions = this.headerOptions.set('Content-Type', 'application/json-patch+json');
    return this._http.post(this.url + '/single-update', parkingInfo, { headers: headerOptions });
  }

  singleInsert(parkingInfo: ParkingInfo) {
    const headerOptions = this.headerOptions.set('Content-Type', 'application/json-patch+json');
    return this._http.put(this.url + '/single-insert', parkingInfo, { headers: headerOptions });
  }

  setDisableDutyCallRange(dutyCallRange: DisableDutyCallRange) {
    const headerOptions = this.headerOptions.set('Content-Type', 'application/json-patch+json');
    return this._http.put(this.url + '/set-disable-duty-call-range', dutyCallRange, { headers: headerOptions });
  }

  updateInstructionUrl(parkingId: string, file: File) {
    const formData: FormData = new FormData();
    formData.append('Id', parkingId);
    formData.append('InstructionFile', file);

    return this._http.put(this.url + '/update-instruction-url', formData, { headers: {
      'Accept': '*/*',
      'AccessCode': localStorage.getItem('AccessCode') ? localStorage.getItem('AccessCode') : ""
    }});
  }

  deleteContent(parkingId: string, path: string, name: string) {
    return this._http.delete(this.url + '/delete?parkingId=' + parkingId + '&path=' + encodeURIComponent(path) + '&name=' + encodeURIComponent(name), { headers: this.headerOptions });
  }

  createFolder(parkingId: string, path: string, name: string) {
    let newFolder ={
      parkingId: parkingId,
      path: path,
      name: name
    }
    return this._http.post(this.url + '/create-folder', newFolder, { headers: this.headerOptions });
  }

  GetAccessCode(accessCode: string) {
    return this._http.get<any>(this.url + "/get-access-code?value=" + accessCode, {headers: this.headerOptions}) as Observable<T>;
  }

  SendConfirmationCode(accessCode: string) {
    return this._http.get<any>(this.url + "/send-confirmation?value=" + accessCode, {headers: this.headerOptions}) as Observable<T>;
  }

  ConfirmLogin(accessCode: string, code: string) {
    return this._http.get<any>(this.url + "/confirm-login?value=" + accessCode + "&code=" + code, {headers: this.headerOptions}) as Observable<T>;
  }

  GetStaff():Observable<T> {
    return this._http.get(this.url + "/get-staff", {headers: this.headerOptions}) as Observable<T>;
  }

  GetMyInfo(accessCode: string) {
    return this._http.get<any>(this.url + "/get-my-info", {
      headers: new HttpHeaders({  
        'Accept': '*/*',
        'AccessCode': accessCode,
        'Content-Type': 'application/json'
      })
    }) as Observable<T>;
  }

  Get(id:number):Observable<T> {
    return this._http.get(this.url + '/get-by-id?id='+ id, {headers: this.headerOptions}) as Observable<T>;
  } 

  async Post(data: T){
    return await this._http.post(this.url + '/insert', data, {headers: this.headerOptions}).toPromise();
  }
  
  Put(data: T | T[]){
    return this._http.put(this.url + '/update', data, {headers: this.headerOptions}).toPromise();
  } 
  async Delete(id: string){
    return await this._http.delete(this.url+ '/delete?id='+ id, {headers: this.headerOptions}).toPromise();
  }

  async Upsert(data: T){
    return await this._http.post(this.url + '/upsert', data, {headers: this.headerOptions}).toPromise();
  }

  async DeleteParkingAlertSubscription(alert){
    let path = "/delete?clientId=" + alert.clientId;
    if(alert.parkingId) {
      path += "&parkingId=" + alert.parkingId;
    } 
    if(alert.parkingAlertTypeId) {
      path +="&parkingAlertTypeId=" + alert.parkingAlertTypeId;
    }
    return await this._http.delete(this.url + path, {headers: this.headerOptions}).toPromise();
  }
}
